import * as React from 'react';
import { useContext, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Avatar, Badge, TableContainer, TableCell, TableHead, Table, TableRow, TableBody } from "@mui/material";
import { Grid, Paper } from "@mui/material";
import { Shield } from "@mui/icons-material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import DashboardContext from '../context/DashboardContext';
import Dashboard from './Dashboard';

export default function DashboardContent() {
  const { chartData, earningData, statsData, downlineData,pcountData  } = useContext(DashboardContext);
  const [user, setUser] = useState(null);
  const userRank = pcountData?.Rank || "DEFAULT"; // Get rank from backend

// Define colors based on rank
const rankColors = {
  STAR: { border: "gold", badge: "gold" },
  BRONZE: { border: "#cd7f32", badge: "#cd7f32" },
  PEARL: { border: "#e5e4e2", badge: "#e5e4e2" },
  DEFAULT: { border: "white", badge: "white" }
};

// Get dynamic styles based on rank
const { border, badge } = rankColors[userRank] || rankColors.DEFAULT;


  useEffect(() => {
    const sessionUser = JSON.parse(sessionStorage.getItem("user"));
    setUser(sessionUser);
  }, []);

  return (
    <Dashboard>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          {/* Combined Stats and Profile Row */}
          <Grid container item xs={12} spacing={2}>
            {/* Stats Section */}
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                {statsData.map((stat, index) => (
                  <Grid item xs={12} sm={4} md={4} key={index}>
                    <Paper sx={{
                      p: 2,
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}>
                      {/* Progress Indicator */}
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        backgroundColor: `${stat.color}20`,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: `${stat.progress}%`,
                          backgroundColor: stat.color,
                          transition: 'width 0.5s ease'
                        }
                      }} />

                      <Box display="flex" alignItems="center" gap={2}>
                        {/* Icon Container */}
                        <Box sx={{
                          backgroundColor: `${stat.color}15`,
                          p: 1,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {React.cloneElement(stat.icon, {
                            sx: {
                              color: stat.color,
                              fontSize: 20
                            }
                          })}
                        </Box>

                        {/* Stat Details */}
                        <Box>
                          <Typography variant="caption" sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            letterSpacing: 0.4,
                            textTransform: 'none',
                            fontSize: "0.8rem"
                          }}>
                            {stat.title}
                          </Typography>
                          <Typography variant="h6" sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            mt: 0.5
                          }}>
                            {stat.value}
                          </Typography>
                        </Box>

                        {/* Rank Advancement Icon */}
                        {stat.rankIcon && (
                          <Box sx={{ ml: 'auto' }}>
                            {React.cloneElement(stat.rankIcon, {
                              sx: {
                                color: stat.color,
                                fontSize: 24
                              }
                            })}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12} container spacing={3}>

              <Grid item xs={12} sx={{ mt: 1.5 }}> {/* Add margin-top here */}
  <Paper sx={{
    p: 3,
    borderRadius: 3,
    background: 'linear-gradient(145deg, #ffffff, #f9f9f9)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    position: 'relative',
    mb: 3
  }}>

    <Grid container spacing={3}>

      <Grid item xs={12} md={4}>
        <Box sx={{
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #299cdb20, #299cdb10)',
          border: '1px solid #299cdb30',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 20px rgba(41, 156, 219, 0.15)'
          }
        }}>
          <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>TOTAL INCOME</Typography>
          <Typography variant="h4" sx={{ 
            mt: 1, 
            fontWeight: 700, 
            color: '#299cdb',
            display: 'flex',
            alignItems: 'center' 
          }}>
            Nrs {pcountData?.TotalCumulativeIncome ?? '0'}
            {/* <Box component="span" sx={{ 
              ml: 1, 
              fontSize: '0.9rem', 
              color: '#00c853',
              backgroundColor: '#00c85315',
              p: '2px 8px',
              borderRadius: 1
            }}>
              +12%
            </Box> */}
          </Typography>
        </Box>
      </Grid>
      
   
      <Grid item xs={12} md={4}>
        <Box sx={{
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #0ab39c20, #0ab39c10)',
          border: '1px solid #0ab39c30',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 20px rgba(10, 179, 156, 0.15)'
          }
        }}>
          <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>DIRECT INCOME </Typography>
          <Typography variant="h4" sx={{ 
            mt: 1, 
            fontWeight: 700, 
            color: '#0ab39c',
            display: 'flex',
            alignItems: 'center' 
          }}>
            Nrs {pcountData?.DirectIncome ?? '0'}
            {/* <Box component="span" sx={{ 
              ml: 1, 
              fontSize: '0.9rem', 
              color: '#00c853',
              backgroundColor: '#00c85315',
              p: '2px 8px',
              borderRadius: 1
            }}>
              +8%
            </Box> */}
          </Typography>
        </Box>
      </Grid>
      
    
      <Grid item xs={12} md={4}>
        <Box sx={{
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f0654820, #f0654810)',
          border: '1px solid #f0654830',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 20px rgba(240, 101, 72, 0.15)'
          }
        }}>
          <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>INDIRECT INCOME </Typography>
          <Typography variant="h4" sx={{ 
            mt: 1, 
            fontWeight: 700, 
            color: '#f06548',
            display: 'flex',
            alignItems: 'center' 
          }}>
            Nrs {pcountData?.IndirectIncome ?? '0'}
            {/* <Box component="span" sx={{ 
              ml: 1, 
              fontSize: '0.9rem', 
              color: '#00c853',
              backgroundColor: '#00c85315',
              p: '2px 8px',
              borderRadius: 1
            }}>
              +15%
            </Box> */}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </Paper>
</Grid>
</Grid>
  
            </Grid>
            

            {/* Profile Section */}
            <Grid item xs={14} md={3}>
  <Paper sx={{ p: 2, borderRadius: "8px", textAlign: "center", overflow: "hidden" }}>
    {/* Profile Image */}
    <Badge
  overlap="circular"
  anchorOrigin={{ vertical: "top", horizontal: "right" }}
  badgeContent={
    pcountData.RankImageURL && (
      <img 
        src={pcountData.RankImageURL} 
        style={{ width: 65, height: 65 }} 
        alt="Rank badge" 
      />
    )
  }
>
  <Avatar
    src={user?.AcntLst?.[0]?.Image || ""}
    sx={{ width: 100, height: 100, border: `3px solid ${border}`, mb: 1 }}
  />
</Badge>
    {/* User Name & Join Year */}
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {user?.AcntLst?.[0]?.FirstName ? `${user.AcntLst[0].FirstName} ${user.AcntLst[0].LastName}` : "Guest"}
    </Typography>
    <Typography variant="body2" color="text.secondary">
    {user?.AcntLst?.[0]?.Username}
    </Typography>

    {/* Rank Achievement */}
    <Paper sx={{ mt: 1, p: 1.5, bgcolor: "black", color: "white", borderRadius: 2, minHeight: 165 }}>
  <Typography variant="body1" sx={{ fontWeight: 700, color: "yellow" }}>
    RANK ACHIEVEMENT
  </Typography>

    <Grid container spacing={0.1} sx={{ mt: 0 }}>
      {/* Show RSB rank if available, otherwise show empty state */}
      <Grid item xs={6} sx={{ textAlign: "center" }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {pcountData.RsbImageURL ? (
            <>
              <img src={pcountData.RsbImageURL} style={{ width: 100, height: 100 }} />
              <Typography variant="body2">{pcountData.RsbRank ?? 'UnRanked'}</Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: "gray", mt: 4 }}>
              RSB Rank Not Available
            </Typography>
          )}
        </Box>
      </Grid>
      
      {/* Always show LSB rank if income condition is met */}
      <Grid item xs={6} sx={{ textAlign: "center" }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {pcountData.LsbImageUrl && (
            <img src={pcountData.LsbImageUrl} style={{ width: 100, height: 100 }} />
          )}
          <Typography variant="body2">
            {pcountData.LsbRank ?? 'UnRanked'}
          </Typography>
        </Box>
      </Grid>
    </Grid>
</Paper>

    {/* User Stats */}
    <Box sx={{ mt: 2, textAlign: "left", display: "grid", gap: 0.3 }}>
      {[
        ["ACTIVE STATUS", pcountData.ActiveStatus || "INACTIVE", pcountData.ActiveStatus === "Active" ? "green" : "red"],
        ["SPONSOR ID", pcountData.SponserID || 0],
        ["CURRENT WEEK", pcountData.CurrentWeek || 0],
        ["SUN TEAM SALES", pcountData.SunTeamSales || 0],
        ["MOON TEAM SALES", pcountData.MoonTeamSales || 0],
        ["FOMB CARRY SUN", pcountData.FOMBCarrySun || 0],
        ["FOMB CARRY MOON", pcountData.FOMBCarryMoon || 0],
        ["TOB CARRY SUN", pcountData.TOBCarrySun || 0],
        ["TOB CARRY MOON", pcountData.TOBCarryMoon || 0],
      ].map(([label, value, color], index) => (
        <Typography key={index} variant="body2" sx={{ display: "flex", justifyContent: "space-between" }}>
          <span>{label}</span>
          <span style={{ color: color || "black" }}>{value}</span>
        </Typography>
      ))}
    </Box>
  </Paper>
</Grid>

          </Grid>

          

          {/* Charts Row
          <Grid item xs={12} container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 3, height: 350 }}>
                <Typography variant="h6" mb={2}>Month Wise Transaction</Typography>
                <ResponsiveContainer width="100%" height="80%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="uv" fill="#299cdb" name="Sales Volume" />
                    <Bar dataKey="pv" fill="#0ab39c" name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 3, height: 350 }}>
                <Typography variant="h6" mb={2}>Month Wise Earning</Typography>
                <ResponsiveContainer width="100%" height="80%">
                  <BarChart data={earningData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="pv" fill="#f06548" name="Target Revenue" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="uv" fill="#299cdb" name="Actual Revenue" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid> */}

          {/* Team Members Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3, background: 'linear-gradient(145deg, #ffffff, #f9f9f9)', overflow: 'hidden', position: 'relative' }}>
              <Typography variant="h6" mb={3} sx={{ fontWeight: 700, color: '#2c3e50', fontSize: '1.5rem', position: 'relative' }}>
                Team Members
              </Typography>
              {/* Team Members Table */}
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="team table">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#2FA8EF', '& th': { fontWeight: 600, color: '#fff', fontSize: '1rem', padding: '12px 16px' } }}>
                      <TableCell>Image</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Team Rank</TableCell>
                      <TableCell>Rank Icon</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {downlineData && downlineData.map((member, index) => (
                      <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 }, bgcolor: index % 2 === 0 ? '#ffffff' : '#f9f9f9', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)', bgcolor: '#f1f1f1' } }}>
                        <TableCell>
                          <Box sx={{ width: 50, height: 50, borderRadius: '50%', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                            <img src={member.Image} alt={member.FullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '1rem', color: '#2c3e50', fontWeight: 500 }}>{member.FullName}</TableCell>
                        <TableCell sx={{ color: '#7f8c8d', fontWeight: 500 }}>{member.Rank ?? 'Unranked'}</TableCell>
                        <TableCell>
                          <Box sx={{ width: 85, height: 85, borderRadius: '50%', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
                            <img src={member.RankImage} alt={member.Rank} style={{ width: '140%', height: '140%',margin: '-17px', objectFit: 'cover' }} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Dashboard>
  );
}